import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { lightTheme, themeFor } from './theme';
import {
  ColorModeProvider,
  useColorModeContext,
} from './contexts/ColorModeContext';
import Booking from 'pages/Booking';
import Quote from 'pages/Quote';
import InquiryReply from 'pages/InquiryReply';
import Profile from 'pages/Profile';
import Review from 'pages/Review';
import EditReview from 'pages/EditReview';
import MessageThread from 'pages/MessageThread';
import Landing from 'pages/Landing';
import LandingV2 from 'pages/LandingV2';
import Account from 'pages/Account';
import PaymentComplete from 'pages/PaymentComplete';
import NotFound from 'pages/NotFound';

// Public client-facing routes — shared via bio links / SMS to people who are
// not the provider, so they must render light regardless of the visitor's
// system / app color preference. We override BOTH layers: the styled-components
// ThemeProvider AND the CSS custom properties set by `body.dark` (see
// `.force-light` in global.css).
const LightOnlyRoute = ({ children }: { children: React.ReactNode }) => (
  <ThemeProvider theme={lightTheme}>
    <div className="force-light">{children}</div>
  </ThemeProvider>
);

function ThemedRoutes() {
  const { mode } = useColorModeContext();
  return (
    <ThemeProvider theme={themeFor(mode)}>
      <BrowserRouter>
        <Routes>
          {/* The Expo app is served from the same host by nginx on its own
              paths (/login, /home, …) — see deploy/site-nginx.conf. */}
          <Route path="/" element={<LandingV2 />} />
          <Route path="/v1" element={<Landing />} />
          <Route path="/v2" element={<LandingV2 />} />
          <Route
            path="/p/:token"
            element={
              <LightOnlyRoute>
                <Profile />
              </LightOnlyRoute>
            }
          />
          <Route
            path="/review/:token"
            element={
              <LightOnlyRoute>
                <Review />
              </LightOnlyRoute>
            }
          />
          <Route path="/reviews/edit" element={<EditReview />} />
          <Route
            path="/reviews/inquiry/:token"
            element={
              <LightOnlyRoute>
                <InquiryReply />
              </LightOnlyRoute>
            }
          />
          <Route
            path="/bookings/:viewToken"
            element={
              <LightOnlyRoute>
                <Booking />
              </LightOnlyRoute>
            }
          />
          <Route
            path="/quotes/:viewToken"
            element={
              <LightOnlyRoute>
                <Quote />
              </LightOnlyRoute>
            }
          />
          <Route
            path="/messages/thread/:viewToken"
            element={
              <LightOnlyRoute>
                <MessageThread />
              </LightOnlyRoute>
            }
          />
          <Route path="/account" element={<Account />} />
          <Route path="/payment-complete" element={<PaymentComplete />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <ColorModeProvider>
      <ThemedRoutes />
    </ColorModeProvider>
  );
}
